import React from 'react';
import { View, Text, Animated, ScrollView, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';

import BackButton from '@components/common/backButton';
import TouchableElement from '@components/common/touchableElement';
import Line from '@components/common/line';
import EditField from '@components/common/editField';

import { notchTop, scale as initialScale } from '@components/frame';

import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { modalFieldBg, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageToolsMobile } from '@components/clampingTool/hooks/useManageToolsMobile';

export default ({
  font = initialFont,
  scale = initialScale,
  clampingTools: clampingToolsState,
  C,
  largeView,
  refetchTools,
  route,
}) => {
  const {
    showEdit,
    editConfig,
    fadeAnimsRef,
    toggleEdit,
    toggleAdd,
    getItemTime,
    getItemDetails,
    onItemPress,
    onLineClick,
    onEditSubmit,
  } = useManageToolsMobile({ clampingTools: clampingToolsState, C, refetchTools, route });

  const { clampingTools } = clampingToolsState;

  if (!clampingTools.length) {
    return (
      <View style={{ flex: 1, minHeight: '100%', justifyContent: 'center', alignItems: 'center' }}>
        <TouchableOpacity
          testID={TEST_IDS.clampingTool.emptyAddButton}
          onPress={toggleAdd}
          style={{
            height: scale(53),
            width: scale(300),
            borderRadius: scale(12),
            backgroundColor: C('Button/Light/Primary'),
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text style={[font.Headline, { color: C('Button/Light/Primary Text') }]}>{i18n.t('Add Tool')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return [
    <View
      style={[
        { width: '100%', height: '100%', backgroundColor: C('Background/Light/Detail') },
        !largeView && { borderRadius: scale(SCREEN_BORDER_RADIUS), backgroundColor: C('Background/Light/Secondary on detail') },
      ]}
      key="ClampingToolList"
    >
      <View style={{ height: '100%' }}>
        {largeView ? (
          <View style={{ marginTop: notchTop }}>
            <View style={{ marginLeft: scale(8) }}>
              <BackButton {...{ text: route?.params?.ncFile }} />
            </View>
            <View
              style={{
                marginTop: scale(24),
                paddingHorizontal: scale(18),
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Text style={[font['Large Title'], { color: C('Label/Light/Primary') }]}>{i18n.t('Tools')}</Text>
              <TouchableElement onPress={toggleAdd}>
                {({ boxStyle }) => (
                  <View style={[boxStyle]}>
                    <Svg icon="plus" width={scale(16)} height={scale(16)} color={'Link/Light/Special'} />
                  </View>
                )}
              </TouchableElement>
            </View>
          </View>
        ) : (
          <View
            style={{
              width: '100%',
              paddingHorizontal: scale(21),
              paddingVertical: scale(21),
              justifyContent: 'flex-end',
              flexDirection: 'row',
            }}
          >
            <TouchableElement testID={TEST_IDS.clampingTool.topAddButton} onPress={toggleAdd}>
              {({ boxStyle }) => (
                <View style={[boxStyle]}>
                  <Text style={[font.Headline, { color: C('Button/Light/Primary') }]}>{i18n.t('Add Tool')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
        )}
        <ScrollView contentContainerStyle={{ paddingBottom: scale(130) }}>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(largeView ? 21 : 8) }}>
            {clampingTools.map((item) => {
              const time = getItemTime(item);
              const details = getItemDetails(item);

              return (
                <View style={{ marginTop: scale(19) }} key={item.clampingToolId}>
                  <TouchableElement testID={TEST_IDS.clampingTool.toolItem(item.clampingToolId)} onPress={() => onItemPress(item)}>
                    {({ boxStyle }) => (
                      <View style={[boxStyle, { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: scale(15) }]}>
                        <View style={{ flexDirection: 'row', width: '70%' }}>
                          <Text numberOfLines={1} style={{ width: '100%' }}>
                            <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>T{item.toolNumber}</Text>
                            <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}> {item.toolName}</Text>
                          </Text>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', width: '30%', justifyContent: 'flex-end' }}>
                          <Text style={[font.Body, { color: C('Label/Light/Tertiary'), marginRight: scale(12) }]}>{time}</Text>

                          <Animated.View
                            style={{
                              transform: [
                                {
                                  rotate: fadeAnimsRef.current[item.clampingToolId]?.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: ['0deg', '90deg'],
                                  }),
                                },
                              ],
                            }}
                          >
                            <Svg icon="chevronRight" width={scale(7)} height={scale(11)} color={'Link/Light/Special'} />
                          </Animated.View>
                        </View>
                      </View>
                    )}
                  </TouchableElement>
                  <Animated.View
                    style={{
                      marginTop: scale(5),
                      height: fadeAnimsRef.current?.[item.clampingToolId]?.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, scale(140)],
                      }),
                      overflow: 'hidden',
                    }}
                  >
                    {details.map((line, index) => (
                      <Line
                        {...{
                          bg: modalFieldBg,
                          item: {
                            ...line,
                            testID: TEST_IDS.clampingTool.detailLine(item.clampingToolId, line.name),
                            onClick: () => onLineClick(line, item),
                          },
                          textColor: line.textColor,
                          index,
                          array: details,
                          C,
                          roundTop: index === 0,
                          roundBottom: index === details.length - 1,
                        }}
                        key={`${item.clampingToolId}-${line.name}`}
                      />
                    ))}
                  </Animated.View>
                </View>
              );
            })}
          </View>
        </ScrollView>
      </View>
    </View>,
    showEdit && (
      <EditField
        key="EditField"
        {...{
          C,
          showModal: showEdit,
          onClose: toggleEdit,
          header: editConfig.header,
          initData: editConfig.initData,
          rounded: true,
          onSubmit: onEditSubmit,
          inputs: editConfig.inputs,
        }}
      />
    ),
  ];
};
